import React from 'react';
import { createPortal } from 'react-dom';
import X from 'lucide-react/dist/esm/icons/x.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Info from 'lucide-react/dist/esm/icons/info.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Zap from 'lucide-react/dist/esm/icons/zap.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '../contexts/TranslationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { formatCurrency } from '../utils/formatCurrency';
import { parseUtcIso } from '../utils/parseUtcIso';
import { getDrawIanaTimeZone, classifyDrawTiming } from '../utils/dateFormat';
import { EARLY_BIRD_STYLES } from './sections/AdditionalPrizesShowcase';
import RichContentRenderer from './RichContentRenderer';

const LOCALE_MAP = { en: 'en-CA', fr: 'fr-CA', es: 'es' };

const formatDetailDateTime = (dateStr, draw, language) => {
  if (!dateStr) return '';
  const date = parseUtcIso(dateStr);
  if (!date) return String(dateStr);

  const resolvedLocale = LOCALE_MAP[language] || 'en-CA';
  const ianaTz = getDrawIanaTimeZone(draw);

  const options = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };

  if (ianaTz) {
    options.timeZone = ianaTz;
    options.timeZoneName = 'short';
  }

  return date.toLocaleDateString(resolvedLocale, options);
};

export default function AdditionalDrawDetailModal({ isOpen, onClose, draw, mainDraw = null, earlyBirdStyle = 'gold' }) {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { language } = useLanguage();

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, onClose]);

  if (!isOpen || !draw) return null;

  const handleBuyTickets = () => {
    onClose();
    navigate('/purchase');
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const prizeValue = Number(draw.prize_value ?? draw.current_jackpot ?? 0);
  const formattedPrize = formatCurrency(prizeValue, { language });
  const timing = classifyDrawTiming(draw, mainDraw);
  const badgeStyle = EARLY_BIRD_STYLES[earlyBirdStyle] || EARLY_BIRD_STYLES.gold;

  const openingDate = formatDetailDateTime(draw.opening_date, draw, language);
  const cutoffDate = formatDetailDateTime(draw.draw_cutoff_date, draw, language);
  const drawDate = formatDetailDateTime(draw.draw_date, draw, language);
  const ticketLimit = draw.number_of_tickets ?? draw.numberOfTickets ?? null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4 sm:p-6 animate-fade-in"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-draw-title"
    >
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col transform transition-all">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-50 via-white to-gray-50 dark:from-gray-800/80 dark:via-gray-900 dark:to-gray-800/80 border-b border-gray-200 dark:border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10 dark:bg-amber-400/10 border border-amber-500/20">
              <Gift className="w-5 h-5 text-amber-500 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                {t('bonusDraws.details', 'Draw Details')}
              </h2>
              <p className="text-xs text-gray-500 dark:text-white/50">
                {timing.isEarlyBird ? t('bonusDraws.earlyBirdDraw', 'Early Bird Opportunity') : t('bonusDraws.bonusDrawOpportunity', 'Bonus Prize Opportunity')}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 overflow-y-auto space-y-6">
          
          {/* Prize Image & Hero Banner */}
          {draw.image_url ? (
            <div className="relative rounded-2xl overflow-hidden bg-gray-950 border border-gray-200 dark:border-white/10 shadow-md">
              <img
                src={draw.image_url}
                alt={draw.title}
                className="w-full h-56 sm:h-64 object-cover"
                onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
              
              {/* Badges on image */}
              <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                {timing.isEarlyBird && (
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border shadow-lg ${badgeStyle.badge}`}>
                    <Zap className={`w-3.5 h-3.5 ${badgeStyle.icon} fill-current`} />
                    {t('bonusDraws.earlyBird', 'Early Bird')}
                  </span>
                )}
                {timing.isConcurrent && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-blue-600 text-white shadow-lg">
                    <Calendar className="w-3.5 h-3.5" />
                    {t('bonusDraws.drawnWithMain', 'Drawn with Main Draw')}
                  </span>
                )}
              </div>

              {formattedPrize && (
                <div className="absolute bottom-3 right-3 text-right">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-white/80 block">
                    {t('bonusDraws.prizeValue', 'Prize Value')}
                  </span>
                  <span className="text-2xl sm:text-3xl font-black text-white drop-shadow-md">
                    {formattedPrize}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent border border-amber-500/20 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {timing.isEarlyBird ? (
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold border ${badgeStyle.badge}`}>
                      <Zap className={`w-3 h-3 ${badgeStyle.icon} fill-current`} />
                      {t('bonusDraws.earlyBird', 'Early Bird')}
                    </span>
                  ) : timing.isConcurrent ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/30">
                      <Calendar className="w-3 h-3" />
                      {t('bonusDraws.drawnWithMain', 'Drawn with Main Draw')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-600 dark:text-purple-400 border border-purple-500/30">
                      <Trophy className="w-3 h-3" />
                      {t('bonusDraws.bonusPrize', 'Bonus Prize')}
                    </span>
                  )}
                </div>
                <h3 id="modal-draw-title" className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">
                  {draw.title}
                </h3>
              </div>

              {formattedPrize && (
                <div className="text-right shrink-0">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-white/60 block">
                    {t('bonusDraws.prizeValue', 'Prize Value')}
                  </span>
                  <span className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400">
                    {formattedPrize}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Title & Description if image existed */}
          {draw.image_url && (
            <div>
              <h3 id="modal-draw-title" className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {draw.title}
              </h3>
              {draw.description && (
                <RichContentRenderer
                  html={draw.description}
                  variant="banner"
                  size="sm"
                  className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                />
              )}
            </div>
          )}

          {!draw.image_url && draw.description && (
            <RichContentRenderer
              html={draw.description}
              variant="banner"
              size="sm"
              className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
            />
          )}

          {/* Draw Information Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Draw Cutoff */}
            {cutoffDate && (
              <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/10 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <span className="text-xs text-gray-500 dark:text-white/60 font-semibold uppercase tracking-wider">
                    {t('bonusDraws.cutoffDate', 'Ticket Cutoff')}
                  </span>
                </div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">
                  {cutoffDate}
                </div>
                <p className="text-[11px] text-gray-500 dark:text-white/50 mt-1">
                  {t('bonusDraws.cutoffNotice', 'Must purchase tickets before this time to qualify')}
                </p>
              </div>
            )}

            {/* Draw Date & Time */}
            {drawDate && (
              <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/10 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Calendar className="w-4 h-4 text-purple-500" />
                  <span className="text-xs text-gray-500 dark:text-white/60 font-semibold uppercase tracking-wider">
                    {t('bonusDraws.drawDate', 'Draw Date & Time')}
                  </span>
                </div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">
                  {drawDate}
                </div>
                <p className="text-[11px] text-gray-500 dark:text-white/50 mt-1">
                  {timing.isConcurrent
                    ? t('bonusDraws.drawsWithMainNotice', 'Winner drawn alongside the main jackpot')
                    : timing.isEarlyBird
                    ? t('bonusDraws.earlyBirdNotice', 'Early bird draw takes place before the main draw')
                    : t('bonusDraws.scheduledDrawNotice', 'Official draw time for this bonus prize')}
                </p>
              </div>
            )}

            {/* Draw Number */}
            {draw.draw_number != null && (
              <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/10 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Ticket className="w-4 h-4 text-indigo-500" />
                  <span className="text-xs text-gray-500 dark:text-white/60 font-semibold uppercase tracking-wider">
                    {t('bonusDraws.drawNumber', 'Draw Number')}
                  </span>
                </div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">
                  #{draw.draw_number}
                </div>
              </div>
            )}

            {/* Ticket Limit */}
            {ticketLimit && (
              <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/10 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Ticket className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-gray-500 dark:text-white/60 font-semibold uppercase tracking-wider">
                    {t('bonusDraws.ticketLimit', 'Ticket Limit')}
                  </span>
                </div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">
                  {t('bonusDraws.maxPerPerson', 'Max per person')}: {ticketLimit}
                </div>
              </div>
            )}
          </div>

          {/* Automatic Entry Notice */}
          <div className="bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-transparent border border-blue-500/20 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-blue-500/20 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5">
                <Info className="w-4 h-4" />
              </div>
              <div className="text-xs text-blue-900 dark:text-blue-100 leading-relaxed">
                <strong className="font-semibold block mb-0.5">
                  {t('bonusDraws.automaticEntryTitle', 'Automatic Entry Included')}
                </strong>
                {t('bonusDraws.autoEntry', 'Every ticket purchase automatically enters you into all eligible bonus draws without any extra cost!')}
              </div>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-800/80 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500 dark:text-white/60 text-center sm:text-left">
            {t('bonusDraws.automaticEntry', 'Automatic entry with any ticket purchase')}
          </p>
          <button
            onClick={handleBuyTickets}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-base font-bold text-white shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, var(--charity-primary, #dc2626), var(--charity-secondary, #b91c1c))',
            }}
          >
            <Ticket className="w-4 h-4" />
            <span>{t('bonusDraws.buyTickets', 'Get Tickets Now')}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
